/**
 * 承認フローバッジの純ロジック (承認モード集約・ラベル・配色)。
 *
 * cells 配列だけに依存する pure 関数群を SubStepEditor から切り出したもの。
 * Controller render prop 内で hooks を呼べないため pure 関数で表現する。
 * test surface を exported な interface に一致させ、承認モード集約・ラベル・
 * 配色を F1 純関数テスト (`approvalBadge.test.ts`) で固定する。
 */
import {
  ApprovalMode,
  BusinessProcess,
} from '@ebalance/proto/bfftypes/v1/approval_flow_type_pb'
import type { LinkedPageKind } from '@ebalance/proto/bffuser/v1/workflow_template_model_pb'

import { linkedPageKindToBusinessProcess } from '~/pages/AdminWorkflowTemplates/models/approvalFlow'

/**
 * 承認フローバッジの集約結果。
 * 全 cell が同一モードなら単一モード、 混在なら 'mixed'、 情報なし (= 非承認対象 kind) は undefined。
 */
export type ApprovalBadgeSummary =
  | {
      mode: ApprovalMode.APPROVAL | ApprovalMode.AUTO | ApprovalMode.NO_APPROVAL
    }
  | { mode: 'mixed' }
  | undefined

/** processes の簡易型 (proto 依存あり = BusinessProcess enum を正確に使う) */
export type ApprovalProcesses = Array<{
  businessProcess: BusinessProcess
  cells: Array<{ approvalMode: ApprovalMode }>
}>

/**
 * 承認バッジサマリを計算する pure 関数。
 * Controller render prop 内で hooks を呼べないため pure 関数で表現する。
 *
 * - linkedPageKind が承認対象 7 kind のいずれかなら BusinessProcess を解決し cells を集約
 * - 非対象 kind (DASHBOARD / CUSTOMER_* 等) → undefined
 */
export function resolveApprovalBadgeSummary(
  linkedPageKind: LinkedPageKind | undefined,
  processes: ApprovalProcesses | undefined,
): ApprovalBadgeSummary {
  if (linkedPageKind === undefined) return undefined
  const businessProcess = linkedPageKindToBusinessProcess(linkedPageKind)
  if (businessProcess === undefined) return undefined
  if (processes === undefined) return undefined

  const setting = processes.find((p) => p.businessProcess === businessProcess)
  if (setting === undefined || setting.cells.length === 0) return undefined

  const modes = setting.cells.map((c) => c.approvalMode)
  const uniqueModes = new Set(
    modes.filter((m) => m !== ApprovalMode.UNSPECIFIED),
  )
  if (uniqueModes.size === 0) return undefined
  if (uniqueModes.size === 1) {
    const single = [...uniqueModes][0]
    if (
      single === ApprovalMode.APPROVAL ||
      single === ApprovalMode.AUTO ||
      single === ApprovalMode.NO_APPROVAL
    ) {
      return { mode: single }
    }
  }
  return { mode: 'mixed' }
}

/** ApprovalBadgeSummary → 表示ラベル (i18n key の文字列を t() に渡す) */
export function approvalBadgeLabel(
  summary: ApprovalBadgeSummary,
  t: (key: string) => string,
): string | undefined {
  if (summary === undefined) return undefined
  if (summary.mode === 'mixed') {
    return t('page:adminWorkflowTemplates.subStep.approvalMixed')
  }
  switch (summary.mode) {
    case ApprovalMode.APPROVAL:
      return t('page:adminWorkflowTemplates.subStep.approvalRequired')
    case ApprovalMode.AUTO:
      return t('page:adminWorkflowTemplates.subStep.approvalAuto')
    case ApprovalMode.NO_APPROVAL:
      return t('page:adminWorkflowTemplates.subStep.approvalNoApproval')
    default:
      return undefined
  }
}

/**
 * ApprovalBadgeSummary → badge の背景色・文字色 (design `.badge` の各 variant トークン)。
 * 要承認 = badge-warn / 承認なし = badge-info / 自動・混在 = badge-neutral。
 *
 * カテゴリカル配色 (承認モード) の色マップを本 model に閉じ込め、 palette prop を
 * 外部公開しない。 将来の token 化はこの 1 箇所で行う。
 */
export function approvalBadgeColors(summary: ApprovalBadgeSummary):
  | {
      background: string
      color: string
    }
  | undefined {
  if (summary === undefined) return undefined
  switch (summary.mode) {
    case ApprovalMode.APPROVAL:
      // badge-warn: surface-warning-low / text-warning-high
      return { background: '#fffcf0', color: '#5d4e00' }
    case ApprovalMode.NO_APPROVAL:
      // badge-info: surface-info-low / text-info-high
      return { background: '#edf1ff', color: '#1e327d' }
    case ApprovalMode.AUTO:
    case 'mixed':
      // badge-neutral: surface-neutral-low / text-neutral-high
      return { background: '#f8f8f8', color: 'rgba(0, 0, 0, 0.74)' }
    default:
      return undefined
  }
}
